import dayjs from "dayjs";
import { Alert, AlertDescription } from "@altimateai/lego";
import { RelativeComponentLoader } from "@/components/loader";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useResourceOpportunities } from "@/pages/sql-warehouses/hooks/useDatabricksOpportunities";
import { InsightSection } from "@/modules/insights";
import type { ResourceType } from "@/pages/explorer/types";

// OpportunityResourceValue.QUERY_HASH = 1 in the backend enum. Opportunities
// extracted for a Databricks statement live alongside Snowflake query
// opportunities in ``individual_opportunities``/``global_opportunities_aggregated``,
// keyed by ``resource_id = statement_id`` + ``resource_type = QUERY_HASH``.
const QUERY_RESOURCE_TYPE = 1 as ResourceType;

interface Props {
  statementId: string;
  workspaceId?: string;
}

/**
 * Recommendations section for a single Databricks query. Mirrors the Snowflake
 * query detail page so filters, sort, and selection behave identically across
 * the two clouds.
 */
const DatabricksQueryOpportunities = ({ statementId, workspaceId }: Props) => {
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const selectedOpportunityId = searchParams.get("opportunityId") || "";

  const {
    startDate: opportunityStartDate,
    endDate: opportunityEndDate,
    onUpdate: onDateRangeChange,
  } = useDateFromSearchParams(
    dayjs().subtract(1, "month").toDate(),
    dayjs().toDate(),
    "opportunity"
  );

  const opportunities = useResourceOpportunities({
    resourceUri: statementId,
    resourceType: QUERY_RESOURCE_TYPE,
    workspaceId,
    startDate: opportunityStartDate,
    endDate: opportunityEndDate,
  });

  const setOpportunityId = (opportunityId: string) => {
    updateSearchParamsByKey({ opportunityId });
  };

  // Don't let an in-flight (or failed) fetch render as an empty InsightSection
  // — InsightSection's empty state says "no opportunities" and would mask both
  // a loading spinner and a real request error. Mirrors ClusterOpportunities.
  if (opportunities.isLoading) {
    return <RelativeComponentLoader label="Loading recommendations..." componentHeight={40} />;
  }

  if (opportunities.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {opportunities.error instanceof Error
            ? opportunities.error.message
            : "Failed to load recommendations"}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <InsightSection
      opportunities={opportunities.data?.data || []}
      selectedOpportunityId={selectedOpportunityId}
      setOpportunityId={setOpportunityId}
      startDate={opportunityStartDate}
      endDate={opportunityEndDate}
      onDateRangeChange={onDateRangeChange}
      showActionsDropdown
      showTimeSavings
    />
  );
};

export default DatabricksQueryOpportunities;
